import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api';

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [report, setReport] = useState(null);
  const [findings, setFindings] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingReport, setUploadingReport] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const fileRef = useRef();
  const reportFileRef = useRef();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchAll();
  }, [id]);

  const fetchAll = async () => {
    try {
      const [caseRes, uploadsRes] = await Promise.all([
        API.get(`/cases/${id}`),
        API.get(`/uploads/${id}`)
      ]);
      setCaseData(caseRes.data);
      setUploads(uploadsRes.data);
    } catch (err) {}
    try {
      const reportRes = await API.get(`/reports/${id}`);
      setReport(reportRes.data);
      setFindings(reportRes.data.findings || '');
    } catch (err) {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  };

  const handleUpload = async () => {
    const file = fileRef.current.files[0];
    if (!file) return showMsg('Please select a file first', 'error');
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await API.post(`/uploads/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      fileRef.current.value = '';
      await fetchAll();
      showMsg('ECG data uploaded successfully');
    } catch (err) {
      showMsg(err.response?.data?.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadECG = async (uploadId, fileName) => {
    try {
      const res = await API.get(`/uploads/download/${uploadId}`, { responseType: 'blob' });
      triggerDownload(res.data, fileName);
    } catch (err) {
      showMsg('Download failed', 'error');
    }
  };

  const handleUploadReport = async () => {
    const file = reportFileRef.current.files[0];
    if (!file) return showMsg('Please select a PDF file', 'error');
    if (!file.name.endsWith('.pdf')) return showMsg('Only PDF files are allowed for reports', 'error');
    setUploadingReport(true);
    const formData = new FormData();
    formData.append('report', file);
    try {
      await API.post(`/reports/${id}/upload-pdf`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      reportFileRef.current.value = '';
      await fetchAll();
      showMsg('Report PDF uploaded successfully');
    } catch (err) {
      showMsg(err.response?.data?.message || 'Report upload failed', 'error');
    } finally {
      setUploadingReport(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const res = await API.get(`/reports/${id}/download-pdf`, { responseType: 'blob' });
      triggerDownload(res.data, `Report_${caseData?.patient_name}_${id}.pdf`);
    } catch (err) {
      showMsg('Report not available yet', 'error');
    }
  };

  const triggerDownload = (data, fileName) => {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSaveFindings = async () => {
    setSaving(true);
    try {
      await API.post(`/reports/${id}`, { findings });
      showMsg('Findings saved');
      await fetchAll();
    } catch (err) {
      showMsg('Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!report?.report_pdf_path) {
      return showMsg('Please upload the final PDF report before publishing', 'error');
    }
    try {
      await API.post(`/reports/${id}/publish`);
      showMsg('Report published — clinic can now download it');
      await fetchAll();
    } catch (err) {
      showMsg('Publish failed', 'error');
    }
  };

  const statusColor = (s) => {
    if (s === 'pending_upload') return { bg: '#fef3c7', text: '#d97706' };
    if (s === 'uploaded') return { bg: '#dbeafe', text: '#1d4ed8' };
    if (s === 'report_ready') return { bg: '#dcfce7', text: '#16a34a' };
    return { bg: '#f3f4f6', text: '#6b7280' };
  };

  if (!caseData) return <div style={{ padding: '40px', color: '#888' }}>Loading...</div>;

  const sc = statusColor(caseData.status);

  return (
    <div style={styles.container}>
      {/* Header */}
      <button style={styles.back} onClick={() => navigate('/cases')}>← Back to Cases</button>
      <div style={styles.titleRow}>
        <div>
          <h2 style={styles.title}>{caseData.patient_name}</h2>
          <div style={styles.metaRow}>
            <span style={styles.metaItem}>DOB: {caseData.patient_dob?.slice(0, 10)}</span>
            <span style={styles.metaDot}>|</span>
            <span style={styles.metaItem}>Gender: {caseData.patient_gender}</span>
            <span style={styles.metaDot}>|</span>
            <span style={styles.metaItem}>Patch: {caseData.patch_serial}</span>
            <span style={styles.metaDot}>|</span>
            <span style={{ ...styles.statusBadge, background: sc.bg, color: sc.text }}>
              {caseData.status?.replace(/_/g, ' ')}
            </span>
          </div>
        </div>
      </div>

      {/* Notification */}
      {msg.text && (
        <div style={{ ...styles.msg, background: msg.type === 'error' ? '#fff0f0' : '#f0fdf4', color: msg.type === 'error' ? '#e53e3e' : '#16a34a' }}>
          {msg.text}
        </div>
      )}

      {/* ── SECTION 1: ECG Data Upload (clinic + admin) ── */}
      {(user.role === 'clinic' || user.role === 'admin') && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>ECG Data Upload</h3>
              <p style={styles.sectionDesc}>Upload the raw ECG data file from the holter patch device</p>
            </div>
            <span style={styles.stepBadge}>Step 1</span>
          </div>
          <div style={styles.uploadRow}>
            <input ref={fileRef} type="file" accept=".pdf,.edf,.csv,.zip,.txt" style={styles.fileInput} />
            <button style={styles.uploadBtn} onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload ECG File'}
            </button>
          </div>
          {uploads.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p style={styles.smallLabel}>Uploaded ECG files ({uploads.length})</p>
              {uploads.map(u => (
                <div key={u.id} style={styles.fileRow}>
                  <div style={styles.fileInfo}>
                    <span style={styles.fileIcon}>📄</span>
                    <div>
                      <div style={styles.fileName}>{u.file_name}</div>
                      <div style={styles.fileDate}>{new Date(u.uploaded_at).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── SECTION 2: ECG Download + Analysis (analyst + admin) ── */}
      {(user.role === 'analyst' || user.role === 'admin') && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>ECG Data & Analysis</h3>
              <p style={styles.sectionDesc}>Download ECG file, analyse in your software, then upload the final PDF report</p>
            </div>
            <span style={styles.stepBadge}>Step 2</span>
          </div>

          {/* ECG files with download */}
          {uploads.length === 0 ? (
            <div style={styles.emptyBox}>No ECG files uploaded by clinic yet</div>
          ) : (
            <div style={{ marginBottom: '20px' }}>
              <p style={styles.smallLabel}>ECG files from clinic</p>
              {uploads.map(u => (
                <div key={u.id} style={styles.fileRow}>
                  <div style={styles.fileInfo}>
                    <span style={styles.fileIcon}>📄</span>
                    <div>
                      <div style={styles.fileName}>{u.file_name}</div>
                      <div style={styles.fileDate}>{new Date(u.uploaded_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <button style={styles.downloadBtn} onClick={() => handleDownloadECG(u.id, u.file_name)}>
                    Download ECG
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Findings text */}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.smallLabel}>Findings / Notes (optional — for internal reference)</label>
            <textarea
              style={styles.textarea}
              placeholder="Enter your findings, observations, and diagnosis here..."
              value={findings}
              onChange={e => setFindings(e.target.value)}
              rows={5}
            />
            <button style={styles.saveBtn} onClick={handleSaveFindings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Findings'}
            </button>
          </div>

          {/* Report PDF upload */}
          <div style={styles.reportUploadBox}>
            <div style={styles.reportUploadHeader}>
              <div>
                <div style={styles.reportUploadTitle}>Upload Final Report PDF</div>
                <div style={styles.reportUploadDesc}>Upload the completed interpretation PDF to share with the clinic</div>
              </div>
              {report?.report_pdf_path && (
                <span style={styles.pdfReadyBadge}>PDF uploaded</span>
              )}
            </div>
            <div style={styles.uploadRow}>
              <input ref={reportFileRef} type="file" accept=".pdf" style={styles.fileInput} />
              <button style={styles.uploadPdfBtn} onClick={handleUploadReport} disabled={uploadingReport}>
                {uploadingReport ? 'Uploading...' : report?.report_pdf_path ? 'Replace PDF' : 'Upload PDF'}
              </button>
            </div>
            {report?.report_pdf_path && (
              <p style={{ fontSize: '12px', color: '#16a34a', marginTop: '8px' }}>
                Report PDF is ready. You can now publish it to the clinic.
              </p>
            )}
          </div>

          {/* Publish button */}
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              style={{ ...styles.publishBtn, opacity: report?.report_pdf_path ? 1 : 0.5 }}
              onClick={handlePublish}
              title={!report?.report_pdf_path ? 'Upload the PDF report first' : ''}
            >
              {caseData.status === 'report_ready' ? 'Report Published' : 'Publish Report to Clinic'}
            </button>
          </div>
        </div>
      )}

      {/* ── SECTION 3: Report Download (clinic) ── */}
      {user.role === 'clinic' && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <div>
              <h3 style={styles.sectionTitle}>Final Report</h3>
              <p style={styles.sectionDesc}>Download your ECG interpretation report once it is ready</p>
            </div>
            <span style={styles.stepBadge}>Step 2</span>
          </div>

          {caseData.status === 'report_ready' && report?.status === 'published' ? (
            <div style={styles.reportReadyBox}>
              <div style={styles.reportReadyIcon}>✅</div>
              <div style={{ flex: 1 }}>
                <div style={styles.reportReadyTitle}>Your report is ready</div>
                <div style={styles.reportReadyDesc}>
                  The analysis has been completed. Click the button to download your PDF report.
                </div>
                {report.published_at && (
                  <div style={styles.reportReadyDate}>
                    Published on {new Date(report.published_at).toLocaleString()}
                  </div>
                )}
              </div>
              <button style={styles.downloadReportBtn} onClick={handleDownloadReport}>
                Download Report PDF
              </button>
            </div>
          ) : (
            <div style={styles.pendingBox}>
              <div style={styles.pendingIcon}>⏳</div>
              <div>
                <div style={styles.pendingTitle}>Report in progress</div>
                <div style={styles.pendingDesc}>
                  Our analysts are reviewing your ECG data. You will be notified once the report is ready.
                </div>
              </div>
            </div>
          )}

          {/* Also show findings if published */}
          {report?.status === 'published' && report?.findings && (
            <div style={{ marginTop: '16px' }}>
              <p style={styles.smallLabel}>Analyst notes</p>
              <div style={styles.findingsBox}>{report.findings}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '860px', margin: '0 auto', padding: '32px 24px' },
  back: { background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '13px', marginBottom: '12px', padding: 0 },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', marginBottom: '8px' },
  metaRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  metaItem: { fontSize: '13px', color: '#666' },
  metaDot: { color: '#ccc', fontSize: '13px' },
  statusBadge: { padding: '3px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  msg: { padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' },
  section: { background: '#fff', padding: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '20px', border: '1px solid #f0f0f0' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  sectionTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', marginBottom: '4px' },
  sectionDesc: { fontSize: '13px', color: '#888' },
  stepBadge: { background: '#eef2ff', color: '#4f46e5', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  uploadRow: { display: 'flex', gap: '12px', alignItems: 'center' },
  fileInput: { flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '13px' },
  uploadBtn: { padding: '10px 20px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  fileRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f8f9fb', borderRadius: '8px', marginBottom: '8px' },
  fileInfo: { display: 'flex', alignItems: 'center', gap: '10px' },
  fileIcon: { fontSize: '20px' },
  fileName: { fontSize: '14px', fontWeight: '500', color: '#1a1a2e' },
  fileDate: { fontSize: '12px', color: '#aaa', marginTop: '2px' },
  downloadBtn: { padding: '7px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' },
  smallLabel: { fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  emptyBox: { background: '#f8f9fb', padding: '16px', borderRadius: '8px', color: '#aaa', fontSize: '13px', textAlign: 'center' },
  textarea: { width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical', outline: 'none', lineHeight: '1.6', marginBottom: '10px' },
  saveBtn: { padding: '8px 20px', background: '#f8f9fb', color: '#333', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  reportUploadBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px', marginTop: '8px' },
  reportUploadHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  reportUploadTitle: { fontSize: '14px', fontWeight: '600', color: '#166534', marginBottom: '2px' },
  reportUploadDesc: { fontSize: '12px', color: '#4ade80' },
  pdfReadyBadge: { background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  uploadPdfBtn: { padding: '10px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap' },
  publishBtn: { padding: '11px 28px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' },
  reportReadyBox: { display: 'flex', alignItems: 'center', gap: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '20px' },
  reportReadyIcon: { fontSize: '28px' },
  reportReadyTitle: { fontSize: '15px', fontWeight: '600', color: '#166534', marginBottom: '4px' },
  reportReadyDesc: { fontSize: '13px', color: '#4ade80' },
  reportReadyDate: { fontSize: '12px', color: '#86efac', marginTop: '4px' },
  downloadReportBtn: { padding: '11px 24px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' },
  pendingBox: { display: 'flex', alignItems: 'center', gap: '16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '20px' },
  pendingIcon: { fontSize: '28px' },
  pendingTitle: { fontSize: '15px', fontWeight: '600', color: '#92400e', marginBottom: '4px' },
  pendingDesc: { fontSize: '13px', color: '#b45309' },
  findingsBox: { background: '#f8f9fb', padding: '14px', borderRadius: '8px', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap', color: '#444' },
};